import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Camera, Check, Copy, Crown, Download, Link2, Loader2, MessageCircle, QrCode, Trash2, UserPlus, Users, X } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import axios from "axios";
import { AppHeader } from "@/components/AppHeader";
import { Avatar } from "@/components/Avatar";
import { FamilyTree } from "@/components/FamilyTree";
import { useFamily } from "@/lib/useFamily";
import { formatDate } from "@/lib/dates";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GROUPS = [
    { id: "grandparent", label: "Grandparents" },
    { id: "parent", label: "Parents" },
    { id: "child", label: "Kids" },
    { id: "sibling", label: "Siblings" },
    { id: "other", label: "Family" },
];

const RELATIONSHIP_LABELS = {
    grandparent: "Grandparent",
    parent: "Parent",
    child: "Kid",
    sibling: "Sibling",
    other: "Family",
};

const INPUT_CLASS =
    "w-full rounded-2xl border-2 border-warmgray bg-[#FDFBF9] px-5 py-3 text-sm font-medium text-navy outline-none transition-colors duration-300 focus:border-peach";

function InviteModal({ onClose, onCreated }) {
    const [label, setLabel] = useState("");
    const [creating, setCreating] = useState(false);
    const [invite, setInvite] = useState(null);
    const [copied, setCopied] = useState(false);

    const inviteUrl = invite ? `${window.location.origin}/join/${invite.token}` : "";

    const create = async (e) => {
        e.preventDefault();
        setCreating(true);
        try {
            const { data } = await axios.post(`${API}/families/invites`, { label }, { withCredentials: true });
            setInvite(data);
            onCreated();
        } catch (err) {
            toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not create invite.");
        }
        setCreating(false);
    };

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(inviteUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {}
    };

    const shareText = `Join our family archive on Ago GenApp: ${inviteUrl}`;

    return (
        <div data-testid="invite-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-navy/40 px-4 backdrop-blur-sm" onClick={onClose}>
            <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
                className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-3xl border-2 border-warmgray bg-ivory p-8"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between">
                    <h2 className="font-display text-2xl">Invite Family Member</h2>
                    <button data-testid="close-invite-modal" onClick={onClose} aria-label="Close" className="text-navy/60 hover:text-navy">
                        <X size={20} />
                    </button>
                </div>

                {!invite ? (
                    <form onSubmit={create} className="mt-5 space-y-3">
                        <p className="text-sm leading-relaxed text-[#2C3347]">
                            Create a personal invite link. Each link works once — name it so you know who it's for.
                        </p>
                        <input
                            data-testid="invite-label-input"
                            type="text"
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                            placeholder="Who is this for? e.g. Aunt Anna"
                            aria-label="Invite label"
                            className={INPUT_CLASS}
                        />
                        <button
                            data-testid="invite-create-button"
                            type="submit"
                            disabled={creating}
                            className="btn-gradient flex w-full items-center justify-center gap-2 rounded-full py-3.5 text-sm font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
                        >
                            {creating && <Loader2 size={15} className="animate-spin" />}
                            Create invite link
                        </button>
                    </form>
                ) : (
                    <div className="mt-5 space-y-4">
                        <div className="rounded-2xl border-2 border-warmgray bg-cream p-4">
                            <p className="break-all font-mono text-xs text-navy" data-testid="invite-link-text">{inviteUrl}</p>
                        </div>
                        <div className="flex justify-center rounded-2xl border-2 border-warmgray bg-white p-5">
                            <QRCodeSVG data-testid="invite-qr-code" value={inviteUrl} size={160} fgColor="#1D2433" />
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                            <button
                                data-testid="invite-copy-button"
                                onClick={copy}
                                className="flex items-center justify-center gap-1.5 rounded-full border-2 border-warmgray py-2.5 text-xs font-semibold text-navy transition-colors duration-300 hover:border-peach"
                            >
                                {copied ? <Check size={13} /> : <Copy size={13} />} Copy
                            </button>
                            <a
                                data-testid="invite-sms-button"
                                href={`sms:?&body=${encodeURIComponent(shareText)}`}
                                className="flex items-center justify-center gap-1.5 rounded-full border-2 border-warmgray py-2.5 text-xs font-semibold text-navy transition-colors duration-300 hover:border-peach"
                            >
                                <MessageCircle size={13} /> SMS
                            </a>
                            <a
                                data-testid="invite-whatsapp-button"
                                href={`https://wa.me/?text=${encodeURIComponent(shareText)}`}
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-center justify-center gap-1.5 rounded-full border-2 border-warmgray py-2.5 text-xs font-semibold text-navy transition-colors duration-300 hover:border-peach"
                            >
                                <MessageCircle size={13} /> WhatsApp
                            </a>
                        </div>
                        <button
                            data-testid="invite-done-button"
                            onClick={onClose}
                            className="btn-gradient w-full rounded-full py-3 text-sm font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5"
                        >
                            Done
                        </button>
                    </div>
                )}
            </motion.div>
        </div>
    );
}

function ManagedProfileModal({ onClose, onCreated }) {
    const [name, setName] = useState("");
    const [relationship, setRelationship] = useState("grandparent");
    const [saving, setSaving] = useState(false);

    const create = async (e) => {
        e.preventDefault();
        if (!name.trim()) return;
        setSaving(true);
        try {
            await axios.post(`${API}/families/managed-members`, { name, relationship }, { withCredentials: true });
            toast.success(`${name.trim()} was added to your family.`);
            onCreated();
            onClose();
        } catch (err) {
            toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not create profile.");
        }
        setSaving(false);
    };

    return (
        <div data-testid="managed-profile-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-navy/40 px-4 backdrop-blur-sm" onClick={() => !saving && onClose()}>
            <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
                className="w-full max-w-md rounded-3xl border-2 border-warmgray bg-ivory p-8"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between">
                    <h2 className="font-display text-2xl">Managed Profile</h2>
                    <button data-testid="close-managed-modal" onClick={() => !saving && onClose()} aria-label="Close" className="text-navy/60 hover:text-navy">
                        <X size={20} />
                    </button>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-[#2C3347]">
                    For grandparents or little ones who don't use the app themselves — create their profile and add memories on their behalf.
                </p>
                <form onSubmit={create} className="mt-5 space-y-3">
                    <input
                        data-testid="managed-name-input"
                        type="text"
                        required
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Name — e.g. Grandma Anna"
                        aria-label="Name"
                        className={INPUT_CLASS}
                    />
                    <select
                        data-testid="managed-relationship-select"
                        value={relationship}
                        onChange={(e) => setRelationship(e.target.value)}
                        aria-label="Relationship"
                        className={INPUT_CLASS}
                    >
                        {Object.entries(RELATIONSHIP_LABELS).map(([id, label]) => (
                            <option key={id} value={id}>
                                {label}
                            </option>
                        ))}
                    </select>
                    <button
                        data-testid="managed-create-button"
                        type="submit"
                        disabled={saving || !name.trim()}
                        className="btn-gradient flex w-full items-center justify-center gap-2 rounded-full py-3.5 text-sm font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
                    >
                        {saving && <Loader2 size={15} className="animate-spin" />}
                        Create profile
                    </button>
                </form>
            </motion.div>
        </div>
    );
}

export default function Family() {
    const { checking, user, family } = useFamily();
    const [members, setMembers] = useState([]);
    const [ownerId, setOwnerId] = useState("");
    const [invites, setInvites] = useState([]);
    const [inviteOpen, setInviteOpen] = useState(false);
    const [managedOpen, setManagedOpen] = useState(false);
    const [codeCopied, setCodeCopied] = useState(false);
    const [removingId, setRemovingId] = useState(null);
    const [savingRole, setSavingRole] = useState(false);
    const [familyView, setFamilyView] = useState("list");
    const [pdfLoading, setPdfLoading] = useState(false);
    const [linksFor, setLinksFor] = useState(null);
    const [storage, setStorage] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const navigate = useNavigate();

    const deleteAccount = async () => {
        try {
            await axios.delete(`${API}/users/me`, { withCredentials: true });
            navigate("/");
        } catch (err) {
            toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not delete your account.");
            setConfirmDelete(false);
        }
    };
    const [avatarUploading, setAvatarUploading] = useState(null);
    const avatarInputRef = useRef(null);
    const avatarTargetRef = useRef(null);

    const pickAvatar = (memberId) => {
        avatarTargetRef.current = memberId;
        avatarInputRef.current?.click();
    };

    const onAvatarFile = async (e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        const target = avatarTargetRef.current;
        if (!f || !target) return;
        setAvatarUploading(target);
        try {
            const form = new FormData();
            form.append("file", f);
            const url = target === user.user_id ? `${API}/users/me/avatar` : `${API}/families/members/${target}/avatar`;
            await axios.post(url, form, { withCredentials: true });
            toast.success("Profile photo updated.");
            await load();
        } catch (err) {
            toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not upload photo.");
        }
        setAvatarUploading(null);
    };

    const myRelationship = members.find((m) => m.user_id === user?.user_id)?.relationship || "other";
    const me = members.find((m) => m.user_id === user?.user_id);
    const notifyOn = me ? me.notify_interactions !== false : true;
    const [translationOn, setTranslationOn] = useState(false);

    useEffect(() => {
        if (!checking && family) setTranslationOn(Boolean(family.translation_enabled));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checking]);

    const load = useCallback(async () => {
        const [membersRes, invitesRes, storageRes] = await Promise.all([
            axios.get(`${API}/families/members`, { withCredentials: true }),
            axios.get(`${API}/families/invites`, { withCredentials: true }),
            axios.get(`${API}/families/storage`, { withCredentials: true }),
        ]);
        setMembers(membersRes.data.members);
        setOwnerId(membersRes.data.owner_id);
        setInvites(invitesRes.data.invites);
        setStorage(storageRes.data);
    }, []);

    useEffect(() => {
        if (!checking) load();
    }, [checking, load]);

    const copyCode = async () => {
        try {
            await navigator.clipboard.writeText(family.code);
            setCodeCopied(true);
            setTimeout(() => setCodeCopied(false), 2000);
        } catch {}
    };

    const toggleTranslation = async () => {
        const next = !translationOn;
        setTranslationOn(next);
        try {
            await axios.patch(`${API}/families/settings`, { translation_enabled: next }, { withCredentials: true });
            toast.success(
                next
                    ? "AI translation on — voice stories in other languages will get an English translation."
                    : "AI translation turned off."
            );
        } catch (err) {
            setTranslationOn(!next);
            toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not update setting.");
        }
    };

    const toggleNotify = async () => {
        const next = !notifyOn;
        try {
            await axios.patch(`${API}/families/me`, { notify_interactions: next }, { withCredentials: true });
            setMembers((prev) => prev.map((m) => (m.user_id === user.user_id ? { ...m, notify_interactions: next } : m)));
            toast.success(next ? "Email alerts turned on." : "Email alerts muted for your memories.");
        } catch {
            toast.error("Could not update notification setting.");
        }
    };

    const downloadTreePdf = async () => {
        setPdfLoading(true);
        try {
            const res = await axios.get(`${API}/families/tree.pdf`, { withCredentials: true, responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${(family.name || "family").replace(/\s+/g, "-")}-family-tree.pdf`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success("Your family tree poster is downloading.");
        } catch {
            toast.error("Could not generate the PDF. Please try again.");
        }
        setPdfLoading(false);
    };

    const saveRole = async (e) => {
        const relationship = e.target.value;
        setSavingRole(true);
        try {
            await axios.patch(`${API}/families/me`, { relationship }, { withCredentials: true });
            setMembers((prev) => prev.map((m) => (m.user_id === user.user_id ? { ...m, relationship } : m)));
            toast.success("Your family role was updated.");
        } catch {
            toast.error("Could not update your role.");
        }
        setSavingRole(false);
    };

    const removeMember = async (memberId) => {
        try {
            await axios.delete(`${API}/families/members/${memberId}`, { withCredentials: true });
            toast.success("Member removed.");
            setRemovingId(null);
            await load();
        } catch (err) {
            toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not remove member.");
        }
    };

    if (checking) {
        return (
            <main data-testid="family-loading" className="flex min-h-screen items-center justify-center bg-cream">
                <Loader2 size={32} className="animate-spin text-lavender" />
            </main>
        );
    }

    const isOwner = ownerId === user.user_id;
    const grouped = GROUPS.map((g) => ({
        ...g,
        members: members.filter((m) => (m.relationship || "other") === g.id),
    })).filter((g) => g.members.length > 0);

    return (
        <main data-testid="family-page" className="min-h-screen bg-cream text-navy">
            <AppHeader user={user} />
            <div className="mx-auto max-w-6xl px-6 py-10">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: [0.76, 0, 0.24, 1] }}
                    className="flex flex-wrap items-end justify-between gap-4"
                >
                    <div>
                        <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.3em] text-[#C98A94]">{family.name}</p>
                        <h1 data-testid="family-heading" className="mt-2 font-display text-3xl tracking-tight md:text-4xl">Family Members</h1>
                        <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
                            <label htmlFor="my-role" className="text-[#2C3347]">
                                Your role:
                            </label>
                            <select
                                id="my-role"
                                data-testid="my-role-select"
                                value={myRelationship}
                                onChange={saveRole}
                                disabled={savingRole}
                                className="rounded-full border-2 border-warmgray bg-ivory px-4 py-1.5 text-xs font-semibold text-navy outline-none transition-colors duration-300 focus:border-peach"
                            >
                                {Object.entries(RELATIONSHIP_LABELS).map(([id, label]) => (
                                    <option key={id} value={id}>
                                        {label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                        <button
                            data-testid="invite-member-button"
                            onClick={() => setInviteOpen(true)}
                            className="btn-gradient flex items-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5"
                        >
                            <UserPlus size={16} /> Invite Family Member
                        </button>
                        <button
                            data-testid="managed-profile-button"
                            onClick={() => setManagedOpen(true)}
                            className="flex items-center gap-2 rounded-full border-2 border-navy px-6 py-3.5 text-sm font-semibold text-navy transition-transform duration-300 hover:-translate-y-0.5"
                        >
                            <Users size={16} /> Managed Profile
                        </button>
                    </div>
                </motion.div>

                <div className="mt-6 flex flex-wrap items-center gap-3 rounded-2xl border-2 border-warmgray bg-ivory p-4">
                    <QrCode size={16} className="text-peach" />
                    <p className="flex-1 text-sm text-[#2C3347]">
                        Or share your family code — anyone with an account can join from the app.
                    </p>
                    <button
                        data-testid="family-code-copy"
                        onClick={copyCode}
                        className="flex items-center gap-2 rounded-full border-2 border-warmgray bg-cream px-4 py-1.5 font-mono text-xs font-semibold tracking-widest transition-colors duration-300 hover:border-peach"
                    >
                        {codeCopied ? <Check size={13} /> : <Copy size={13} />} CODE: {family.code}
                    </button>
                </div>

                <section data-testid="family-settings" className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="flex items-center justify-between gap-4 rounded-2xl border-2 border-warmgray bg-ivory p-5">
                        <div>
                            <p className="text-sm font-semibold">AI English translation</p>
                            <p className="mt-1 text-xs leading-relaxed text-[#2C3347]">
                                Voice stories told in other languages get an English translation under the transcript.
                                {!isOwner && " Only the family owner can change this."}
                            </p>
                        </div>
                        <Toggle on={translationOn} onClick={toggleTranslation} disabled={!isOwner} testid="translation-toggle" />
                    </div>
                    <div className="flex items-center justify-between gap-4 rounded-2xl border-2 border-warmgray bg-ivory p-5">
                        <div>
                            <p className="text-sm font-semibold">Email alerts</p>
                            <p className="mt-1 text-xs leading-relaxed text-[#2C3347]">
                                Email me when family members comment on or love my memories.
                            </p>
                        </div>
                        <Toggle on={notifyOn} onClick={toggleNotify} testid="notify-toggle" />
                    </div>
                </section>

                {storage &&
                    (() => {
                        const fmt = (b) => (b >= 1024 ** 3 ? `${(b / 1024 ** 3).toFixed(1)} GB` : `${Math.round(b / 1024 ** 2)} MB`);
                        const pct = Math.min(100, Math.round((storage.used_bytes / storage.quota_bytes) * 100));
                        return (
                            <section data-testid="storage-meter" className="mt-4 rounded-2xl border-2 border-warmgray bg-ivory p-5">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <p className="flex items-center gap-2 text-sm font-semibold">
                                        Family storage
                                        {storage.is_founding && (
                                            <span className="rounded-full bg-gradient-to-r from-lavender to-sunset px-2.5 py-0.5 text-[9px] font-semibold uppercase tracking-widest text-white">
                                                Founding 100 · 1 GB included
                                            </span>
                                        )}
                                    </p>
                                    <p className="font-mono text-xs text-navy/70" data-testid="storage-usage-text">
                                        {fmt(storage.used_bytes)} of {fmt(storage.quota_bytes)}
                                    </p>
                                </div>
                                <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-warmgray">
                                    <div
                                        data-testid="storage-usage-bar"
                                        className="h-full rounded-full bg-gradient-to-r from-lavender via-peach to-sunset transition-[width] duration-500"
                                        style={{ width: `${Math.max(pct, 2)}%` }}
                                    />
                                </div>
                                {pct >= 80 && (
                                    <p className="mt-2 text-xs leading-relaxed text-peach">
                                        Your archive is filling up — write to hello@agogenapp.com and we'll grow your space together.
                                    </p>
                                )}
                            </section>
                        );
                    })()}

                <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
                    <div data-testid="family-view-toggle" className="flex w-fit rounded-full border-2 border-warmgray p-1">
                        {[
                            { id: "list", label: "Members" },
                            { id: "tree", label: "Family Tree" },
                        ].map((v) => (
                            <button
                                key={v.id}
                                data-testid={`family-view-${v.id}`}
                                onClick={() => setFamilyView(v.id)}
                                className={`rounded-full px-5 py-2 text-xs font-semibold transition-colors duration-300 ${
                                    familyView === v.id ? "bg-navy text-white" : "text-navy hover:bg-ivory"
                                }`}
                            >
                                {v.label}
                            </button>
                        ))}
                    </div>
                    <button
                        data-testid="tree-pdf-button"
                        onClick={downloadTreePdf}
                        disabled={pdfLoading}
                        className="flex items-center gap-2 rounded-full border-2 border-navy px-5 py-2.5 text-xs font-semibold text-navy transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
                    >
                        {pdfLoading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                        {pdfLoading ? "Creating poster…" : "Download keepsake PDF"}
                    </button>
                </div>

                {familyView === "tree" && <FamilyTree members={members} currentUserId={user.user_id} />}

                {familyView === "list" &&
                    grouped.map((group) => (
                    <section key={group.id} data-testid={`member-group-${group.id}`} className="mt-10">
                        <h2 className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-[#C98A94]">{group.label}</h2>
                        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            {group.members.map((m) => {
                                const canRemove = !m.is_owner && (isOwner || (m.managed && m.managed_by === user.user_id));
                                const canEditAvatar = m.user_id === user.user_id || (m.managed && (isOwner || m.managed_by === user.user_id));
                                const canManageLinks = isOwner || m.user_id === user.user_id || (m.managed && m.managed_by === user.user_id);
                                return (
                                    <motion.div
                                        key={m.user_id}
                                        data-testid={`member-card-${m.user_id}`}
                                        initial={{ opacity: 0, y: 16 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.5, ease: [0.76, 0, 0.24, 1] }}
                                        className="rounded-3xl border-2 border-warmgray bg-ivory p-5"
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="relative shrink-0">
                                                    <Avatar person={m} size={48} />
                                                    {avatarUploading === m.user_id && (
                                                        <div className="absolute inset-0 flex items-center justify-center rounded-full bg-navy/50" data-testid={`avatar-uploading-${m.user_id}`}>
                                                            <Loader2 size={16} className="animate-spin text-white" />
                                                        </div>
                                                    )}
                                                    {canEditAvatar && avatarUploading !== m.user_id && (
                                                        <button
                                                            data-testid={`avatar-upload-${m.user_id}`}
                                                            onClick={() => pickAvatar(m.user_id)}
                                                            aria-label={`Change photo for ${m.name || m.email}`}
                                                            className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full bg-navy text-white shadow transition-transform duration-300 hover:scale-110"
                                                        >
                                                            <Camera size={12} />
                                                        </button>
                                                    )}
                                                </div>
                                                <div>
                                                    <p className="flex items-center gap-1.5 text-sm font-semibold">
                                                        {m.name || m.email}
                                                        {m.is_owner && (
                                                            <span data-testid={`owner-badge-${m.user_id}`} title="Family owner" className="flex items-center gap-1 rounded-full bg-navy px-2 py-0.5 text-[9px] font-semibold uppercase tracking-widest text-white">
                                                                <Crown size={10} /> Owner
                                                            </span>
                                                        )}
                                                        {m.founding_number && m.founding_number <= 100 && (
                                                            <span
                                                                data-testid={`founding-badge-${m.user_id}`}
                                                                title="One of the first 100 families"
                                                                className="rounded-full bg-gradient-to-r from-lavender to-sunset px-2 py-0.5 text-[9px] font-semibold uppercase tracking-widest text-white"
                                                            >
                                                                Founding #{m.founding_number}
                                                            </span>
                                                        )}
                                                    </p>
                                                    <p className="mt-0.5 font-mono text-[10px] uppercase tracking-widest text-navy/60">
                                                        {RELATIONSHIP_LABELS[m.relationship] || "Family"}
                                                        {m.managed && " · Managed profile"}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2.5">
                                                {canManageLinks && (
                                                    <button
                                                        data-testid={`member-links-${m.user_id}`}
                                                        onClick={() => setLinksFor(m)}
                                                        aria-label={`Manage family links for ${m.name || m.email}`}
                                                        title="Link parents & children"
                                                        className="text-navy/40 transition-colors duration-300 hover:text-lavender"
                                                    >
                                                        <Link2 size={15} />
                                                    </button>
                                                )}
                                                {canRemove &&
                                                    (removingId === m.user_id ? (
                                                        <span className="flex items-center gap-1.5">
                                                            <button
                                                                data-testid={`member-remove-confirm-${m.user_id}`}
                                                                onClick={() => removeMember(m.user_id)}
                                                                className="rounded-full bg-red-700 px-3 py-1.5 text-[10px] font-semibold text-white"
                                                            >
                                                                Confirm
                                                            </button>
                                                            <button
                                                                data-testid={`member-remove-cancel-${m.user_id}`}
                                                                onClick={() => setRemovingId(null)}
                                                                aria-label="Cancel"
                                                                className="text-navy/60 hover:text-navy"
                                                            >
                                                                <X size={14} />
                                                            </button>
                                                        </span>
                                                    ) : (
                                                        <button
                                                            data-testid={`member-remove-${m.user_id}`}
                                                            onClick={() => setRemovingId(m.user_id)}
                                                            aria-label={`Remove ${m.name || m.email}`}
                                                            className="text-navy/40 transition-colors duration-300 hover:text-red-700"
                                                        >
                                                            <Trash2 size={15} />
                                                        </button>
                                                    ))}
                                            </div>
                                        </div>
                                        <div className="mt-4 flex items-center justify-between border-t border-warmgray pt-3">
                                            <p className="font-mono text-[10px] uppercase tracking-widest text-[#2C3347]">
                                                {m.memory_count} {m.memory_count === 1 ? "memory" : "memories"}
                                            </p>
                                            <div className="flex items-center gap-3">
                                                <Link
                                                    to={`/dashboard?author=${m.user_id}`}
                                                    data-testid={`member-memories-${m.user_id}`}
                                                    className="text-xs font-semibold text-navy underline-offset-4 hover:underline"
                                                >
                                                    Created
                                                </Link>
                                                <Link
                                                    to={`/dashboard?tagged=${m.user_id}`}
                                                    data-testid={`member-tagged-${m.user_id}`}
                                                    className="text-xs font-semibold text-navy underline-offset-4 hover:underline"
                                                >
                                                    Tagged in
                                                </Link>
                                            </div>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </section>
                ))}

                <section data-testid="pending-invites" className="mt-12">
                    <h2 className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-[#C98A94]">Invites</h2>
                    {invites.length === 0 ? (
                        <p className="mt-3 rounded-2xl border-2 border-dashed border-warmgray bg-ivory p-6 text-center text-sm text-navy/70">
                            No invites yet. Create one and share it via link, SMS, WhatsApp, or QR code.
                        </p>
                    ) : (
                        <div className="mt-4 space-y-3">
                            {invites.map((inv) => (
                                <div
                                    key={inv.token}
                                    data-testid={`invite-row-${inv.token}`}
                                    className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border-2 border-warmgray bg-ivory px-5 py-3.5"
                                >
                                    <div>
                                        <p className="text-sm font-semibold">{inv.label || "Family invite"}</p>
                                        <p className="mt-0.5 font-mono text-[10px] uppercase tracking-widest text-navy/60">
                                            By {inv.created_by_name} · {formatDate(inv.created_at)}
                                        </p>
                                    </div>
                                    {inv.used_by ? (
                                        <span data-testid={`invite-status-${inv.token}`} className="rounded-full bg-navy px-3.5 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-white">
                                            Accepted{inv.used_by_name ? ` by ${inv.used_by_name}` : ""}
                                        </span>
                                    ) : (
                                        <span className="flex items-center gap-2">
                                            <span data-testid={`invite-status-${inv.token}`} className="rounded-full border-2 border-peach px-3.5 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-peach">
                                                Pending
                                            </span>
                                            <InviteCopyButton token={inv.token} />
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </section>

                <section data-testid="privacy-section" className="mt-12">
                    <h2 className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-[#C98A94]">Privacy</h2>
                    <div className="mt-4 flex flex-wrap items-center justify-between gap-4 rounded-2xl border-2 border-warmgray bg-ivory p-5">
                        <p className="max-w-md text-sm leading-relaxed text-[#2C3347]">
                            Your archive belongs to you. Deleting your account removes your profile and sign-in permanently.
                        </p>
                        {confirmDelete ? (
                            <span className="flex items-center gap-2">
                                <button
                                    data-testid="delete-account-confirm"
                                    onClick={deleteAccount}
                                    className="rounded-full bg-red-700 px-5 py-2.5 text-xs font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5"
                                >
                                    Yes, delete my account
                                </button>
                                <button
                                    data-testid="delete-account-cancel"
                                    onClick={() => setConfirmDelete(false)}
                                    className="rounded-full border-2 border-warmgray px-5 py-2.5 text-xs font-semibold text-navy"
                                >
                                    Keep my account
                                </button>
                            </span>
                        ) : (
                            <button
                                data-testid="delete-account-button"
                                onClick={() => setConfirmDelete(true)}
                                className="rounded-full border-2 border-warmgray px-5 py-2.5 text-xs font-semibold text-navy/70 transition-colors duration-300 hover:border-red-700 hover:text-red-700"
                            >
                                Delete my account
                            </button>
                        )}
                    </div>
                </section>
            </div>

            {inviteOpen && <InviteModal onClose={() => setInviteOpen(false)} onCreated={load} />}
            {managedOpen && <ManagedProfileModal onClose={() => setManagedOpen(false)} onCreated={load} />}
            {linksFor && (
                <LinksModal memberId={linksFor.user_id} members={members} onClose={() => setLinksFor(null)} onChanged={load} />
            )}
            <input
                ref={avatarInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                data-testid="avatar-file-input"
                aria-label="Upload profile photo"
                onChange={onAvatarFile}
            />
        </main>
    );
}

function LinksModal({ memberId, members, onClose, onChanged }) {
    const member = members.find((m) => m.user_id === memberId);
    const [selected, setSelected] = useState("");
    const [saving, setSaving] = useState(false);

    if (!member) return null;
    const parentIds = member.parent_ids || [];
    const parents = members.filter((m) => parentIds.includes(m.user_id));
    const children = members.filter((m) => (m.parent_ids || []).includes(member.user_id));
    const candidates = members.filter((m) => m.user_id !== member.user_id && !parentIds.includes(m.user_id));

    const add = async () => {
        if (!selected) return;
        setSaving(true);
        try {
            await axios.post(`${API}/families/links`, { child_id: member.user_id, parent_id: selected }, { withCredentials: true });
            toast.success("Parent linked to the family tree.");
            setSelected("");
            await onChanged();
        } catch (err) {
            toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not add the link.");
        }
        setSaving(false);
    };

    const remove = async (parentId) => {
        setSaving(true);
        try {
            await axios.delete(`${API}/families/links`, {
                data: { child_id: member.user_id, parent_id: parentId },
                withCredentials: true,
            });
            await onChanged();
        } catch (err) {
            toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not remove the link.");
        }
        setSaving(false);
    };

    return (
        <div data-testid="links-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-navy/40 px-4 backdrop-blur-sm" onClick={() => !saving && onClose()}>
            <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
                className="w-full max-w-md rounded-3xl border-2 border-warmgray bg-ivory p-8"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between">
                    <h2 className="font-display text-2xl">Family links</h2>
                    <button data-testid="close-links-modal" onClick={() => !saving && onClose()} aria-label="Close" className="text-navy/60 hover:text-navy">
                        <X size={20} />
                    </button>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-[#2C3347]">
                    Link parents and children so your family tree shows real relationships.
                </p>

                <div className="mt-6">
                    <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-[#C98A94]">
                        {member.name || member.email} — parents
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                        {parents.length === 0 && <p className="text-xs text-navy/60">No parents linked yet.</p>}
                        {parents.map((p) => (
                            <span
                                key={p.user_id}
                                data-testid={`link-parent-chip-${p.user_id}`}
                                className="flex items-center gap-1.5 rounded-full border-2 border-warmgray bg-cream py-1 pl-1 pr-2.5 text-xs font-semibold text-navy"
                            >
                                <Avatar person={p} size={20} />
                                {p.name || p.email}
                                <button
                                    data-testid={`link-remove-${p.user_id}`}
                                    onClick={() => remove(p.user_id)}
                                    disabled={saving}
                                    aria-label={`Remove ${p.name || p.email} as parent`}
                                    className="text-navy/50 hover:text-red-700"
                                >
                                    <X size={12} />
                                </button>
                            </span>
                        ))}
                    </div>
                    <div className="mt-3 flex gap-2">
                        <select
                            data-testid="link-parent-select"
                            value={selected}
                            onChange={(e) => setSelected(e.target.value)}
                            aria-label="Choose a parent"
                            className="flex-1 rounded-full border-2 border-warmgray bg-[#FDFBF9] px-4 py-2.5 text-xs font-semibold text-navy outline-none transition-colors duration-300 focus:border-peach"
                        >
                            <option value="">Choose a parent…</option>
                            {candidates.map((c) => (
                                <option key={c.user_id} value={c.user_id}>
                                    {c.name || c.email}
                                </option>
                            ))}
                        </select>
                        <button
                            data-testid="link-add-button"
                            onClick={add}
                            disabled={saving || !selected}
                            className="btn-gradient flex items-center gap-1.5 rounded-full px-5 py-2.5 text-xs font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
                        >
                            {saving && <Loader2 size={13} className="animate-spin" />}
                            Add
                        </button>
                    </div>
                </div>

                {children.length > 0 && (
                    <div className="mt-6 border-t border-warmgray pt-4">
                        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-[#C98A94]">
                            Children of {member.name || member.email}
                        </p>
                        <div className="mt-2 flex flex-wrap gap-2">
                            {children.map((ch) => (
                                <span
                                    key={ch.user_id}
                                    data-testid={`link-child-chip-${ch.user_id}`}
                                    className="flex items-center gap-1.5 rounded-full border-2 border-warmgray bg-cream py-1 pl-1 pr-3 text-xs font-semibold text-navy"
                                >
                                    <Avatar person={ch} size={20} />
                                    {ch.name || ch.email}
                                </span>
                            ))}
                        </div>
                        <p className="mt-2 text-[11px] text-navy/50">Children are linked from their own cards.</p>
                    </div>
                )}
            </motion.div>
        </div>
    );
}

function Toggle({ on, onClick, disabled = false, testid }) {
    return (
        <button
            data-testid={testid}
            onClick={disabled ? undefined : onClick}
            role="switch"
            aria-checked={on}
            disabled={disabled}
            className={`relative h-7 w-12 shrink-0 rounded-full transition-colors duration-300 ${
                on ? "bg-navy" : "bg-warmgray"
            } ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
        >
            <span
                className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-all duration-300 ${on ? "left-6" : "left-1"}`}
            />
        </button>
    );
}

function InviteCopyButton({ token }) {
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(`${window.location.origin}/join/${token}`);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {}
    };
    return (
        <button
            data-testid={`invite-copy-${token}`}
            onClick={copy}
            aria-label="Copy invite link"
            className="text-navy/60 transition-colors duration-300 hover:text-navy"
        >
            {copied ? <Check size={15} /> : <Copy size={15} />}
        </button>
    );
}
